import { useEffect, useState } from "react";
import { Users, UserCog, Disc3, Radio, Play, Mail, Loader2, ArrowUpRight } from "lucide-react";
import { StatCard } from "@/components/dashboard/ui";
import { api } from "@/lib/api";
import UpgradeBanner from "@/components/billing/UpgradeBanner";
import HomeSearch from "@/components/dashboard/HomeSearch";
import ReleaseCalendar from "@/components/dashboard/ReleaseCalendar";
import ActivityFeed from "@/components/dashboard/ActivityFeed";

export default function LabelOverview({ ov, goto }) {
  const s = ov || {};
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/home");
        setHome(res.data);
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  }

  const upcoming = home?.upcoming || [];
  const activity = home?.activity || [];
  const stats = home?.stats || {};

  return (
    <div className="space-y-8" data-testid="label-overview">
      <UpgradeBanner recommendedTier="label" goto={goto} />

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">Home</div>
          <h2 className="mt-1 font-display text-3xl md:text-4xl tracking-tight text-white">Your label at a glance</h2>
          <p className="mt-1.5 text-slate-400">Search the catalog, track upcoming releases and see what your team&apos;s been up to.</p>
        </div>
        <button
          data-testid="label-overview-invite"
          onClick={() => goto("roster")}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 transition-colors self-start md:self-auto"
        >
          Invite an artist <ArrowUpRight size={16} />
        </button>
      </div>

      <HomeSearch goto={goto} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Artists" value={s.roster ?? "—"} sub="On your roster" icon={Users} />
        <StatCard label="Team seats" value={s.team ?? "—"} sub="Members" icon={UserCog} />
        <StatCard label="Catalog" value={s.catalog ?? stats.total_releases ?? "—"} sub="Releases" icon={Disc3} />
        <StatCard label="Live" value={s.live ?? stats.live ?? "—"} sub="On stores" icon={Radio} />
        <div className="col-span-2 lg:col-span-1 rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-600/15 to-transparent px-5 py-4">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase font-bold text-blue-300">
            <Play size={12} /> Streams (30d)
          </div>
          <div className="mt-1.5 font-display text-2xl text-white">{(s.total_streams ?? stats.total_streams ?? 0).toLocaleString()}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3">
          <ReleaseCalendar upcoming={upcoming} goto={goto} />
        </div>
        <div className="lg:col-span-2">
          <ActivityFeed events={activity} goto={goto} />
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h3 className="font-display text-xl text-white">Build your roster</h3>
          <p className="mt-1.5 text-sm text-slate-400 max-w-md">Invite artists to your label, add team members, and set how royalties are shared.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="label-overview-invite-cta" onClick={() => goto("roster")} className="inline-flex items-center rounded-full bg-white text-black font-medium px-6 py-3 hover:bg-slate-200 transition-colors">
            Invite an artist
          </button>
          <button data-testid="label-overview-splits" onClick={() => goto("splits")} className="inline-flex items-center rounded-full border border-white/15 px-6 py-3 hover:bg-white/5 transition-colors">
            Edit royalty splits
          </button>
        </div>
        {(s.pending_invites ?? 0) > 0 && (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Mail size={15} className="text-blue-400" />
            {s.pending_invites} pending invitation{s.pending_invites === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
}
